/**
 * <ry-drawer>
 *
 * Slide-out drawer/panel component.
 *
 * Usage:
 * <ry-button drawer="menu">Open Menu</ry-button>
 * <ry-drawer id="menu" side="left">
 *   <h2>Menu</h2>
 *   <nav>...</nav>
 * </ry-drawer>
 *
 * Attributes:
 * - id: Required for trigger binding
 * - side: left (default), right, bottom
 * - open: Initially open
 *
 * JS uses data-ry-target for queries, CSS uses classes for styling.
 */

import { RyElement } from '../core/ry-element.js';

export class RyDrawer extends RyElement {
  #previouslyFocused: Element | null = null;
  #scrollbarWidth = 0;

  setup(): void {
    this.#transformMarkup();

    // Find trigger buttons anywhere in document
    const drawerId = this.id;
    if (drawerId) {
      // drawer="id" attribute on ry-button
      document.querySelectorAll<HTMLElement>(`ry-button[drawer="${drawerId}"], [drawer="${drawerId}"]`).forEach(trigger => {
        this.on(trigger, 'click', () => this.open());
      });
    }

    // Close buttons inside drawer (use [close] attribute)
    this.$$('[close]').forEach(closeBtn => {
      this.on(closeBtn, 'click', () => this.close());
    });

    // Backdrop click
    const backdrop = this.$('[data-ry-target="backdrop"]');
    if (backdrop) {
      this.on(backdrop, 'click', () => this.close());
    }

    // Escape key
    this.on(document, 'keydown', this.#handleKeydown);

    // Calculate scrollbar width
    this.#scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    // Initialize state
    if (this.hasAttribute('open')) {
      this.state = 'open';
    } else if (!this.hasAttribute('data-ry-state')) {
      this.state = 'closed';
    }
  }

  #transformMarkup(): void {
    // Check if already has full markup
    if (this.$('[data-ry-target="panel"]')) return;

    const side = this.getAttribute('side') ?? 'left';
    const content = this.innerHTML;

    // data-ry-target for JS queries, class for CSS styling
    this.innerHTML = `
      <div data-ry-target="backdrop" class="ry-drawer__backdrop"></div>
      <div data-ry-target="panel" class="ry-drawer__panel" role="dialog" aria-modal="true" data-ry-side="${side}">
        <button data-ry-target="close" class="ry-drawer__close" close aria-label="Close">&times;</button>
        <div data-ry-target="content" class="ry-drawer__content">
          ${content}
        </div>
      </div>
    `;

    // Set side attribute for CSS
    this.setAttribute('data-ry-side', side);
  }

  #handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.state === 'open') {
      this.close();
    }
  };

  open(): void {
    if (this.state === 'open') return;

    // Store currently focused element
    this.#previouslyFocused = document.activeElement;

    // Lock body scroll via data attribute (CSS handles the style)
    document.body.setAttribute('data-ry-scroll-lock', '');
    document.body.style.setProperty('--ry-scrollbar-width', `${this.#scrollbarWidth}px`);

    // Open drawer
    this.state = 'open';

    // Trap focus
    const panel = this.$<HTMLElement>('[data-ry-target="panel"]');
    if (panel) {
      this.nextFrame().then(() => {
        this.trapFocus(panel);
      });
    }

    this.emit('open');
  }

  close(): void {
    if (this.state === 'closed') return;

    // Unlock body scroll
    document.body.removeAttribute('data-ry-scroll-lock');
    document.body.style.removeProperty('--ry-scrollbar-width');

    // Close drawer
    this.state = 'closed';

    // Restore focus
    if (this.#previouslyFocused instanceof HTMLElement) {
      this.#previouslyFocused.focus();
      this.#previouslyFocused = null;
    }

    this.emit('close');
  }

  toggle(): void {
    if (this.state === 'open') {
      this.close();
    } else {
      this.open();
    }
  }
}

customElements.define('ry-drawer', RyDrawer);
